import * as z from "zod";
import { validateCoordinatePair } from "@/validation/coordinates";

/**
 * @param isEstimatedDeliveryRequired - Whether the delivery date must be provided.
 * @param isTimeSlotRequired - Whether a delivery time slot must be chosen (order planning enabled on receiver).
 */
export const getLocationAndDeliverySchema = (isEstimatedDeliveryRequired?: boolean, isTimeSlotRequired?: boolean) => {
  return z
    .object({
      packaging: z.number({ message: "validation:boxtypeRequired" }),
      differentAddress: z.boolean().optional(),
      address: z.string().optional(),
      postCode: z.string().optional(),
      countryId: z.number({ message: "validation:countryRequired" }).optional(),
      estimatedDelivery: z
        .date({
          required_error: "validation:estimatedDeliveryRequired",
          invalid_type_error: "validation:estimatedDeliveryRequired",
        })
        .optional()
        .nullable(),
      place: z.string().optional(),
      latitude: z.string().optional(),
      longitude: z.string().optional(),
      capturedAt: z.string().optional().nullable(),
      receptionSlotId: z.number().nullable().optional(),
    })
    .superRefine(
      (
        {
          address,
          postCode,
          countryId,
          place,
          differentAddress,
          estimatedDelivery,
          latitude,
          longitude,
          receptionSlotId,
        },
        refinementContext
      ) => {
        if (differentAddress) {
          if (!address) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "validation:addressRequired",
              path: ["address"],
            });
          }
          if (!postCode) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "validation:postCodeRequired",
              path: ["postCode"],
            });
          }
          if (!countryId) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "validation:countryRequired",
              path: ["countryId"],
            });
          }
          if (!place) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "validation:placeRequired",
              path: ["place"],
            });
          }
        }

        // Handle conditional requirement for estimatedDelivery
        if (isEstimatedDeliveryRequired && !estimatedDelivery) {
          refinementContext.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation:estimatedDeliveryRequired",
            path: ["estimatedDelivery"],
          });
        }

        validateCoordinatePair(latitude, longitude, refinementContext, { keyPrefix: "validation:" });

        if (isTimeSlotRequired && !receptionSlotId) {
          refinementContext.addIssue({
            code: z.ZodIssueCode.custom,
            message: "orders:deliverySlotRequired",
            path: ["receptionSlotId"],
          });
        }
      }
    );
};

// Update types to reflect the schema structure
export type LocationAndDeliveryFormInput = z.infer<ReturnType<typeof getLocationAndDeliverySchema>>;
